import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Badge,
  Text,
  VStack,
  HStack,
  Box,
  Divider,
  Flex,
  Icon,
  useToast,
  ButtonGroup,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { FiCheck, FiClock, FiUser, FiX } from "react-icons/fi";
import { ProcessViewModel } from "../../../models/Process";
import {
  approveHistory,
  rejectHistory,
  getHistoryByEntityId,
} from "../../../services/historyService";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

interface UserViewModel {
  id: string;
  name: string;
  email?: string;
  admin?: boolean;
}

export interface HistoryViewModel {
  id: string;
  entityId: string;
  property: string;
  before: string | null;
  after: string | null;
  changedAt: string;
  changedById: string;
  changedBy: UserViewModel;
  approvedDate: string | null;
  approvedById: string | null;
  approvedBy: UserViewModel | null;
  rejectedDate: string | null;
  rejectedById: string | null;
  rejectedBy: UserViewModel | null;
}

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  process?: ProcessViewModel;
  // Uso genérico (ex.: ordem de compra): entidade e título próprios.
  entityId?: string;
  entity?: string;
  title?: string;
  // Processo só revisa alterações de não-admin; PO permite revisar qualquer alteração.
  reviewAdminChanges?: boolean;
  // onChanged: rejeição desfez um valor (recarregar dados). onReviewed: qualquer revisão (contadores).
  onChanged?: () => void;
  onReviewed?: () => void;
}

export default function ApproveModal({
  isOpen,
  onClose,
  process,
  entityId,
  entity = "Process",
  title,
  reviewAdminChanges = false,
  onChanged,
  onReviewed,
}: HistoryModalProps) {
  const targetId = entityId ?? process?.id;
  const [historyData, setHistoryData] = useState<HistoryViewModel[]>([]);
  const auth = useAuthUser<any>();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const toast = useToast();

  const getHistory = async () => {
    const response = await getHistoryByEntityId(targetId ?? "", entity);
    if (response) {
      setHistoryData(response);
    }
  };

  useEffect(() => {
    if (targetId && isOpen) getHistory();
  }, [targetId, isOpen]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "MMM dd, yyyy HH:mm");
  };

  const renderValue = (value: string | null) => {
    if (value === null || value === "") {
      return (
        <Text color="gray.400" fontStyle="italic">
          Vazio
        </Text>
      );
    }
    return <Text>{value}</Text>;
  };

  const handleApprove = async (historyId: string) => {
    setLoadingId(historyId);
    const response = await approveHistory(historyId);

    if (!response?.success) {
      toast({
        title: response?.notifications?.[0] ?? "Não foi possível concluir.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Aprovado com sucesso!",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await getHistory();
      onReviewed?.();
    }
    setLoadingId(null);
  };

  const handleReject = async (historyId: string) => {
    setLoadingId(historyId);
    const response = await rejectHistory(historyId);

    if (!response?.success) {
      toast({
        title: response?.notifications?.[0] ?? "Não foi possível concluir.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Reprovado com sucesso!",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await getHistory();
      onChanged?.();
      onReviewed?.();
    }
    setLoadingId(null);
  };

  // Verifica se deve mostrar os botões de aprovar/reprovar
  const shouldShowActionButtons = (record: HistoryViewModel) => {
    return (
      auth?.admin === true &&
      (reviewAdminChanges || record.changedBy?.admin === false) &&
      !record.approvedDate &&
      !record.rejectedDate
    );
  };

  // Função para determinar o status visual
  const getRecordStatus = (record: HistoryViewModel) => {
    if (record.approvedDate) return { color: "green", label: "Aprovado" };
    if (record.rejectedDate) return { color: "red", label: "Reprovado" };
    return { color: "blue", label: "Pendente" };
  };
  const handleClose = () => {
    onClose();
    setHistoryData([]);
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="4xl"
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" boxShadow="0 12px 32px rgba(16,24,40,0.18)">
        <ModalHeader borderBottom={crm.border}>{title ?? `Alterações do processo ${process?.cvfRef ?? ""}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {historyData.length === 0 ? (
            <Box py={8} textAlign="center">
              <Text color="gray.500">Ainda não há alterações</Text>
            </Box>
          ) : (
            <VStack align="stretch" spacing={0} position="relative">
              {/* Timeline line */}
              <Box
                position="absolute"
                left="19px"
                top="20px"
                bottom="20px"
                width="2px"
                bg="gray.200"
                zIndex={0}
              />

              {historyData.map((record, index) => {
                const status = getRecordStatus(record);

                return (
                  <Flex key={index} position="relative" pb={6}>
                    {/* Timeline dot */}
                    <Box
                      position="absolute"
                      left="12px"
                      top="8px"
                      width="16px"
                      height="16px"
                      borderRadius="full"
                      bg={`${status.color}.500`}
                      border="3px solid white"
                      boxShadow="0 0 0 2px"
                      borderColor={`${status.color}.500`}
                      zIndex={1}
                    />

                    {/* Content */}
                    <Box ml="50px" flex="1">
                      <Box
                        p={4}
                        bg="white"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="sm"
                        _hover={{ boxShadow: "md" }}
                        transition="all 0.2s"
                      >
                        {/* Header */}
                        <Flex justify="space-between" align="start" mb={3}>
                          <HStack spacing={2}>
                            <Badge colorScheme="blue" fontSize="sm">
                              {record.property}
                            </Badge>
                            {record.approvedDate && (
                              <Badge colorScheme="green" fontSize="xs">
                                <HStack spacing={1}>
                                  <Icon as={FiCheck} />
                                  <Text>Aprovado</Text>
                                </HStack>
                              </Badge>
                            )}
                            {record.rejectedDate && (
                              <Badge colorScheme="red" fontSize="xs">
                                <HStack spacing={1}>
                                  <Icon as={FiX} />
                                  <Text>Reprovado</Text>
                                </HStack>
                              </Badge>
                            )}
                          </HStack>
                          <HStack spacing={1} color="gray.600">
                            <Icon as={FiClock} boxSize={3} />
                            <Text fontSize="xs">
                              {formatDate(record.changedAt)}
                            </Text>
                          </HStack>
                        </Flex>

                        {/* Before/After Changes */}
                        <HStack align="stretch" spacing={3} mb={3}>
                          <Box flex="1">
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              mb={1}
                              fontWeight="medium"
                            >
                              Antes
                            </Text>
                            <Box
                              p={3}
                              bg="red.50"
                              borderRadius="md"
                              borderLeft="3px solid"
                              borderColor="red.400"
                              minH="60px"
                            >
                              {renderValue(record.before)}
                            </Box>
                          </Box>

                          <Flex align="center" pt={6}>
                            <Text fontSize="xl" color="gray.400">
                              →
                            </Text>
                          </Flex>

                          <Box flex="1">
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              mb={1}
                              fontWeight="medium"
                            >
                              Agora
                            </Text>
                            <Box
                              p={3}
                              bg="green.50"
                              borderRadius="md"
                              borderLeft="3px solid"
                              borderColor="green.400"
                              minH="60px"
                            >
                              {renderValue(record.after)}
                            </Box>
                          </Box>
                        </HStack>

                        <Divider my={3} />

                        {/* User Information */}
                        <Flex
                          justify="space-between"
                          align="start"
                          flexWrap="wrap"
                          gap={4}
                        >
                          <Box>
                            <HStack spacing={1} mb={1}>
                              <Icon as={FiUser} boxSize={3} color="gray.600" />
                              <Text
                                fontSize="xs"
                                color="gray.600"
                                fontWeight="medium"
                              >
                                Alterado por
                              </Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="medium">
                              {record.changedBy?.name ?? "Excel"}
                            </Text>
                            {record.changedBy?.email && (
                              <Text fontSize="xs" color="gray.600">
                                {record.changedBy.email}
                              </Text>
                            )}
                          </Box>

                          {record.approvedDate && record.approvedBy && (
                            <Box textAlign="right">
                              <HStack spacing={1} mb={1} justify="flex-end">
                                <Icon
                                  as={FiCheck}
                                  boxSize={3}
                                  color="green.600"
                                />
                                <Text
                                  fontSize="xs"
                                  color="gray.600"
                                  fontWeight="medium"
                                >
                                  Aprovado por
                                </Text>
                              </HStack>
                              <Text fontSize="sm" fontWeight="medium">
                                {record.approvedBy.name}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDate(record.approvedDate)}
                              </Text>
                            </Box>
                          )}

                          {record.rejectedDate && record.rejectedBy && (
                            <Box textAlign="right">
                              <HStack spacing={1} mb={1} justify="flex-end">
                                <Icon as={FiX} boxSize={3} color="red.600" />
                                <Text
                                  fontSize="xs"
                                  color="gray.600"
                                  fontWeight="medium"
                                >
                                  Reprovado por
                                </Text>
                              </HStack>
                              <Text fontSize="sm" fontWeight="medium">
                                {record.rejectedBy.name}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDate(record.rejectedDate)}
                              </Text>
                            </Box>
                          )}
                        </Flex>

                        {/* Botões de Aprovar/Reprovar - Condicional */}
                        {shouldShowActionButtons(record) && (
                          <Flex w="100%" justifyContent="flex-end" mt={3}>
                            <ButtonGroup size="sm" spacing={2}>
                              <Button
                                isLoading={loadingId === record.id}
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<FiX />}
                                onClick={() => handleReject(record.id)}
                              >
                                Reprovar
                              </Button>
                              <Button
                                isLoading={loadingId === record.id}
                                colorScheme="green"
                                leftIcon={<FiCheck />}
                                onClick={() => handleApprove(record.id)}
                              >
                                Aprovar
                              </Button>
                            </ButtonGroup>
                          </Flex>
                        )}
                      </Box>
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
